// The badge parts library in the SPA: loads /badge-art/lib.json once, composes badge
// SVGs with the composer copied from _badges/system (compose.js), and prepares them
// for inline use (per-instance ids, the coin shine layer).
//
// /badge-art/lib.json and compose.js are written by `pnpm badges:sync-art`.
import { useEffect, useSyncExternalStore } from "react";
import { composeFull, composeMini } from "./compose";
import type { Tier } from "../types";

// ---------- library shape (only what the SPA reads; compose.js reads the rest) ----------

export interface BadgeLibTier {
  rank: number;
  base: string;
  size: number;
  miniSize: number;
  numeral: string;
  colors?: Record<string, string>;
  glow?: { color: string; blur: number; grow: number; opacity: number };
  miniNumber?: { fill: string; outline: string };
}

export interface BadgeLibCategory {
  name: string;
  family: string;
  description?: string;
  layers: Record<string, string>;
  miniGlyph: string;
  repeatable?: boolean;
}

export interface BadgeLibBadge {
  code: string;
  category: string;
  number: number | null;
  tier: string;
  name: string;
  description?: string;
  [key: string]: unknown;
}

export interface BadgeLib {
  tokens: {
    canvas: number;
    tiers: Record<string, BadgeLibTier>;
    layout: { center: [number, number]; [key: string]: unknown };
    [key: string]: unknown;
  };
  manifest: Record<string, { center?: [number, number]; [key: string]: unknown }>;
  categories: Record<string, BadgeLibCategory>;
  badges: BadgeLibBadge[];
  parts: Record<string, { viewBox: number[]; body: string }>;
  faces: Record<string, unknown>;
}

export const BADGE_LIB_URL = `${process.env.PUBLIC_URL || ""}/badge-art/lib.json`;

// ---------- loading (one fetch per page load, shared by every badge) ----------

export interface BadgeLibState {
  lib: BadgeLib | null;
  error: Error | null;
  /** True until the library has loaded or failed. */
  loading: boolean;
}

const RETRY_AFTER_MS = 30_000;

let state: BadgeLibState = { lib: null, error: null, loading: true };
let inflight: Promise<BadgeLib> | null = null;
let failedAt = 0;
const listeners = new Set<() => void>();

function setState(next: BadgeLibState) {
  state = next;
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function isBadgeLib(value: unknown): value is BadgeLib {
  const v = value as BadgeLib | null;
  return !!v && typeof v === "object" && !!v.tokens?.tiers && !!v.categories && !!v.parts && !!v.faces && !!v.manifest;
}

/** Load the library (cached at module level; concurrent callers share one request). */
export function loadBadgeLib(): Promise<BadgeLib> {
  if (state.lib) return Promise.resolve(state.lib);
  if (!inflight) {
    if (state.error) setState({ lib: null, error: null, loading: true });
    inflight = fetch(BADGE_LIB_URL)
      .then((res) => {
        if (!res.ok) throw new Error(`badge library: HTTP ${res.status}`);
        return res.json();
      })
      .then((json: unknown) => {
        if (!isBadgeLib(json)) throw new Error("badge library: unexpected shape");
        composeCache.clear();
        setState({ lib: json, error: null, loading: false });
        return json;
      })
      .catch((err: unknown) => {
        inflight = null;
        failedAt = Date.now();
        const error = err instanceof Error ? err : new Error(String(err));
        setState({ lib: null, error, loading: false });
        throw error;
      });
  }
  return inflight;
}

/** The library if it has already loaded, else null. Never fetches. */
export function getLoadedBadgeLib(): BadgeLib | null {
  return state.lib;
}

/**
 * Put a library in the cache without fetching (tests, previews). Passing null
 * resets the cache so the next useBadgeLib() fetches again.
 */
export function primeBadgeLib(lib: BadgeLib | null): void {
  composeCache.clear();
  failedAt = 0;
  if (lib) {
    inflight = Promise.resolve(lib);
    setState({ lib, error: null, loading: false });
  } else {
    inflight = null;
    setState({ lib: null, error: null, loading: true });
  }
}

/**
 * The parts library for React components. Starts the one fetch on first use;
 * every component re-renders when it lands. After a failure it retries at most
 * every 30 s, on the next mount.
 */
export function useBadgeLib(): BadgeLibState {
  const snapshot = useSyncExternalStore(subscribe, () => state, () => state);
  useEffect(() => {
    if (state.lib || inflight) return;
    if (state.error && Date.now() - failedAt < RETRY_AFTER_MS) return;
    loadBadgeLib().catch(() => {
      /* state carries the error; BadgeArt falls back */
    });
  }, []);
  return snapshot;
}

// ---------- tiers ----------

export type BadgeTierKind = "paper" | "rock" | "coin" | "gem";

export interface BadgeTierStyle {
  kind: BadgeTierKind;
  /** Main surface colour of the tier's base. */
  fill: string;
  /** Lighter tint (gloss) of the base. */
  gloss: string;
  /** Readable text colour on `fill`. */
  ink: string;
  /** Glow colour for gems, null otherwise. */
  glow: string | null;
}

/** Tier colours for backdrops, stage lights and the initials fallback. */
export const BADGE_TIER_STYLE: Record<Tier, BadgeTierStyle> = {
  PAPER: { kind: "paper", fill: "#f7f4e2", gloss: "#fdfbf2", ink: "#000000", glow: null },
  ROCK: { kind: "rock", fill: "#666666", gloss: "#b3b3b3", ink: "#ffffff", glow: null },
  BRONZE: { kind: "coin", fill: "#af5700", gloss: "#ffd2a9", ink: "#ffffff", glow: null },
  SILVER: { kind: "coin", fill: "#b3b3b3", gloss: "#f2f2f2", ink: "#000000", glow: null },
  GOLD: { kind: "coin", fill: "#f4bd4d", gloss: "#ffe6bd", ink: "#000000", glow: null },
  RUBY: { kind: "gem", fill: "#c1272d", gloss: "#ef6f78", ink: "#ffffff", glow: "#ff4d5e" },
  EMERALD: { kind: "gem", fill: "#22b573", gloss: "#8fefc1", ink: "#000000", glow: "#2bd98a" },
  DIAMOND: { kind: "gem", fill: "#b1e8e6", gloss: "#d3f2f0", ink: "#000000", glow: "#7ff5ec" },
};

export function tierStyle(tier: string): BadgeTierStyle {
  return BADGE_TIER_STYLE[tier as Tier] ?? BADGE_TIER_STYLE.PAPER;
}

// ---------- composing ----------

export type BadgeArtVariant = "full" | "mini";

export interface BadgeArtSpec {
  category: string;
  tier: Tier | string;
  number: number | null;
  /** Copies held; above 1 the art gets the "x3" counter chip. */
  count?: number;
  /** Badge code; defaults to CATEGORY_NUMBER (or CATEGORY without a number). */
  code?: string;
  variant?: BadgeArtVariant;
}

export function badgeCode(spec: Pick<BadgeArtSpec, "category" | "number" | "code">): string {
  if (spec.code) return spec.code;
  return spec.number === null || spec.number === undefined ? spec.category : `${spec.category}_${spec.number}`;
}

const COMPOSE_CACHE_MAX = 400;
const composeCache = new Map<string, string | null>();

function composeRaw(lib: BadgeLib, spec: BadgeArtSpec): string | null {
  const variant = spec.variant ?? "full";
  const code = badgeCode(spec);
  const repeat = spec.count && spec.count > 1 ? Math.floor(spec.count) : null;
  const key = `${variant}|${code}|${spec.category}|${spec.tier}|${spec.number ?? ""}|${repeat ?? ""}`;
  if (composeCache.has(key)) return composeCache.get(key) ?? null;
  let svg: string | null = null;
  if (lib.categories[spec.category] && lib.tokens.tiers[spec.tier]) {
    try {
      const input = { code, category: spec.category, tier: spec.tier, number: spec.number, repeat };
      svg = variant === "mini" ? composeMini(input, lib) : composeFull(input, lib);
    } catch {
      // a part or a digit the library does not have yet: the caller falls back
      svg = null;
    }
  }
  if (composeCache.size >= COMPOSE_CACHE_MAX) composeCache.clear();
  composeCache.set(key, svg);
  return svg;
}

/** True when the library can draw this badge (category and tier known, every part present). */
export function canComposeBadge(lib: BadgeLib, spec: BadgeArtSpec): boolean {
  return composeRaw(lib, spec) !== null;
}

/**
 * The composed SVG of a badge as a standalone document (the composer's own ids,
 * no animation classes needed), or null when the library cannot draw it.
 * `pixelSize` sets the root width/height, for drawing onto a canvas at that size.
 */
export function composeBadgeSvg(
  lib: BadgeLib,
  spec: BadgeArtSpec,
  opts: { pixelSize?: number } = {}
): string | null {
  const raw = composeRaw(lib, spec);
  if (!raw || !opts.pixelSize) return raw;
  const px = Math.round(opts.pixelSize);
  return raw.replace(/^<svg([^>]*?) width="[^"]*" height="[^"]*"/, `<svg$1 width="${px}" height="${px}"`);
}

/** Same as composeBadgeSvg, loading the library first. Resolves null when it cannot draw it. */
export async function getBadgeSvg(spec: BadgeArtSpec, opts: { pixelSize?: number } = {}): Promise<string | null> {
  const lib = await loadBadgeLib();
  return composeBadgeSvg(lib, spec, opts);
}

/** A data: URL for an SVG string (for new Image() / canvas drawImage). */
export function svgToDataUrl(svg: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

// ---------- inline preparation ----------

/**
 * Prefix every id, url(#id) and href="#id" with `prefix`, so the same badge can
 * sit on a page twice without sharing filter, clip or <use> targets.
 */
export function prefixSvgIds(svg: string, prefix: string): string {
  return svg
    .replace(/(\s)id="([^"]+)"/g, (_m, sp: string, id: string) => `${sp}id="${prefix}-${id}"`)
    .replace(/url\((['"]?)#([^'")]+)\1\)/g, (_m, q: string, id: string) => `url(${q}#${prefix}-${id}${q})`)
    .replace(/(\s(?:xlink:)?href)="#([^"]+)"/g, (_m, attr: string, id: string) => `${attr}="#${prefix}-${id}"`);
}

/**
 * The idle shine for metal coins: a pale band clipped to the base silhouette,
 * drawn over the base and the numeral and under the modifier and tools.
 * CSS moves `.bdg-shine-bar` across; without the idle class it stays off the badge.
 */
export function coinShineLayer(lib: BadgeLib, tier: string): string | null {
  const t = lib.tokens.tiers[tier];
  const part = t && lib.parts[t.base];
  if (!t || !part) return null;
  const [vx, vy, w, h] = part.viewBox;
  const scale = t.size / Math.max(w, h);
  const [cx, cy] = lib.tokens.layout.center;
  const [ox, oy] = lib.manifest[t.base]?.center ?? [vx + w / 2, vy + h / 2];
  const r = (n: number) => Math.round(n * 1000) / 1000;
  const baseT = `translate(${r(cx)} ${r(cy)}) scale(${r(scale)}) translate(${r(-ox)} ${r(-oy)})`;
  const shapes = (part.body.match(/<\w+[^>]*data-layer="silhouette"[^>]*\/>/g) || []).map((el) => {
    const bare = el.replace(/\s(data-role|data-layer|fill|stroke|stroke-width|stroke-miterlimit)="[^"]*"/g, "");
    return /\stransform="/.test(bare)
      ? bare.replace(/\stransform="([^"]*)"/, ` transform="${baseT} $1"`)
      : bare.replace(/\s*\/>$/, ` transform="${baseT}"/>`);
  });
  if (!shapes.length) return null;
  const N = lib.tokens.canvas;
  return (
    `<g class="bdg-shine" clip-path="url(#shine-clip)"><clipPath id="shine-clip">${shapes.join("")}</clipPath>` +
    `<polygon class="bdg-shine-bar" points="-40,-10 6,-10 -84,${N + 10} -130,${N + 10}" fill="#fff" opacity="0.55"/></g>`
  );
}

const LAYER_AFTER_SHINE = /<g id="[^"]*" class="bdg-(?:modifier|tool|tool-front|counter)"/;

/**
 * Turn a composed SVG into the markup BadgeArt injects: no <title> (the wrapper
 * carries the label), aria-hidden, the optional coin shine layer, ids prefixed.
 */
export function prepareInlineSvg(svg: string, prefix: string, opts: { shine?: string | null } = {}): string {
  let out = svg.replace(/<title>[\s\S]*?<\/title>/, "").replace(/^<svg /, '<svg aria-hidden="true" focusable="false" ');
  if (opts.shine) {
    const at = out.search(LAYER_AFTER_SHINE);
    const end = out.lastIndexOf("</svg>");
    const idx = at >= 0 ? at : end;
    out = out.slice(0, idx) + opts.shine + out.slice(idx);
  }
  return prefixSvgIds(out, prefix);
}

/** Up to two initials for the fallback tile: from the name, else from the category. */
const WORD_CHAR = /[A-Za-z0-9À-ɏͰ-ϿЀ-ӿ]/;

export function badgeInitials(name: string | undefined, category: string): string {
  const words = (name || category.replace(/_/g, " ")).split(/\s+/).filter((w) => WORD_CHAR.test(w));
  const letters = words.slice(0, 2).map((w) => (w.match(WORD_CHAR) || [""])[0]);
  return letters.join("").toUpperCase() || "?";
}
